"use client";

import React, { useState, useRef } from 'react';
import styles from './_gc-step-summary.module.scss';
import Button from '@/components/button';
import { FormInputText } from '@/components/form';
import Captcha from '@/components/captcha';
import CustomImage from '@/components/image';
import Modal from '@/components/modal';
import ApiGcocOutputEmail from '@/api-client/gcoc-output-email';
import { compressFvKeys } from '../../utils';

/**
 * @description GC OPTIMIZE™ Calculator — Summary
 * Renders inline (no pill wrapper) once all preceding steps are completed.
 * Shows "Your summary is ready." with View and Email actions.
 * Clicking "Email summary" expands an inline form (single email input).
 */
function GcStepSummary({ state, onSendingChange }) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [captchaInvalid, setCaptchaInvalid] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState('');
  const emailRef = useRef(null);
  const modalRef = useRef(null);
  const { getCaptchaToken, CaptchaDisclaimer, CaptchaErrorMessage } = Captcha();

  const handleEmailSubmit = async () => {
    if (submittingForm) return;

    const currentEmail = (emailRef.current?.value || '').trim();

    if (emailRef.current) {
      emailRef.current.setValue(currentEmail);
      if (!emailRef.current.validate()) {
        return;
      }
    }

    setSubmittingForm(true);
    onSendingChange?.(true);
    setCaptchaInvalid(false);
    setSubmissionError(false);
    setSubmissionErrorMessage('');

    try {
      const captchaToken = await getCaptchaToken();

      const calcId = state.calcId || crypto.randomUUID();

      const formData = {
        calcId,
        summaryData: state.formValues,
        population: state.population,
        status: state.status,
        hceTotal: state.hceTotal,
        hcpEmail: currentEmail,
      };

      const { success, error } = await ApiGcocOutputEmail({ formData, captchaToken });

      if (error) {
        if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
          setCaptchaInvalid(true);
        } else {
          setSubmissionError(true);
          setSubmissionErrorMessage(error?.message || 'There was an error with your submission. Please try again.');
        }
      } else if (success) {
        setShowEmailForm(false);
        modalRef.current?.open();
      }
    } catch (error) {
      if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
        setCaptchaInvalid(true);
      } else {
        setSubmissionError(true);
        setSubmissionErrorMessage(error?.message || 'There was an error with your submission. Please try again.');
      }
    }

    setSubmittingForm(false);
    onSendingChange?.(false);
  };

  return (
    <>
      <div className={`${styles.gc_step_summary}${submittingForm ? ` ${styles.gc_step_summary_sending}` : ''}`}>
        <div className={styles.top_row}>
          <p className={styles.ready_text}>Your summary is ready.</p>
          <Button
            id="gc-summary-view-summary-btn"
            variant={['solid', 'solid--orange']}
            customClass={styles.summary_btn}
            data-gtm-event-category="gc-calculator"
            data-gtm-event-action="Click"
            data-gtm-event-label="View summary"
            href={`/dosing/gc-optimize-calculator-summary?d=${encodeURIComponent(JSON.stringify({ id: state.calcId, fv: compressFvKeys(state.formValues), pop: state.population, st: state.status, hce: state.hceTotal }))}`}
            targetBlank
          >
            View summary
          </Button>
        </div>

        {!showEmailForm && (
          <div className={styles.actions}>
            <Button
              id="gc-summary-email-summary-btn"
              type="button"
              variant={['solid', 'solid--orange']}
              customClass={styles.summary_btn}
              onClick={() => setShowEmailForm(true)}
              data-gtm-event-category="gc-calculator"
              data-gtm-event-action="Click"
              data-gtm-event-label="Email summary"
            >
              <svg width="25" height="16" viewBox="0 0 25 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M0.428589 2.16211V12.6109C0.428589 13.967 1.53815 15.0766 2.89428 15.0766H21.9629C23.319 15.0766 24.4286 13.967 24.4286 12.6109V2.16211" stroke="white" strokeWidth="0.857143" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M10.9655 0.427734L22.9811 0.42777C23.507 0.427772 24.0573 0.614062 24.2635 1.0978C24.6276 1.95199 24.382 2.99814 23.5736 3.57646L17.5259 8.28837" stroke="white" strokeWidth="0.857143" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M11.8121 0.427771L1.91746 0.427737C1.39581 0.427735 0.852534 0.610539 0.633385 1.08393C0.216382 1.98471 0.460187 3.1063 1.31932 3.73447L7.65667 8.372" stroke="white" strokeWidth="0.857143" strokeMiterlimit="10" strokeLinecap="round"/>
                <path d="M21.795 12.1177L14.5364 5.91207C13.4082 4.94775 11.7473 4.94268 10.6132 5.9011L3.26172 12.1177" stroke="white" strokeWidth="0.857143" strokeMiterlimit="10" strokeLinecap="round"/>
              </svg>
              Email summary
            </Button>
          </div>
        )}

        {showEmailForm && (
          <div className={styles.email_form}>
            <hr className={styles.divider} />

            <p className={styles.fields_note}>Field marked with an * is required.</p>

            <div className={styles.input_group}>
              <FormInputText
                ref={emailRef}
                name="hcp-email"
                type="email"
                label="Email"
                placeholder="Please enter email address"
                required
                customClass={styles.email_input}
                maxLength={254}
                errorMessage="Please enter a valid email address."
              />
            </div>

            <p className={styles.disclaimer}>
              By clicking &ldquo;Email summary&rdquo; below, you acknowledge that you have
              read, understood, and agree to our{' '}
              <Button
                targetBlank
                customClass="!italic"
                href="https://www.neurocrine.com/privacy-policy/"
                data-gtm-event-category="gc-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Privacy Policy"
              >Privacy Policy</Button>
              {' '}and{' '}
              <Button
                targetBlank
                customClass="!italic"
                href="https://www.neurocrine.com/terms-conditions/"
                data-gtm-event-category="gc-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Terms and Conditions"
              >Terms and Conditions</Button>.
            </p>

            <CaptchaDisclaimer />

            <div className={styles.form_actions}>
              <button
                type="button"
                className={styles.close_btn}
                onClick={() => setShowEmailForm(false)}
                aria-label="Close email form"
                disabled={submittingForm}
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L13.6797 13.6797M1 13.6797L13.6797 1" stroke="#F06622" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <Button
                type="button"
                variant={['solid', 'solid--orange']}
                customClass={[styles.summary_btn, styles.summary_btn_no_icon].join(' ')}
                onClick={handleEmailSubmit}
                disabled={submittingForm}
                iconType={submittingForm ? 'loading' : undefined}
                data-gtm-event-category="gc-calculator"
                data-gtm-event-action="Click"
                data-gtm-event-label="Email summary submit"
              >
                {submittingForm ? 'Sending...' : 'Email summary'}
              </Button>
            </div>

            {(captchaInvalid || submissionError) && (
              <div className={styles.errors}>
                <CaptchaErrorMessage invalid={captchaInvalid} />
                {submissionError && <p>{submissionErrorMessage || 'There was an error with your submission. Please try again.'}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal ref={modalRef} customClass={styles.summary_success_modal}>
        <CustomImage
          src="/images/components/hcp/gc-calc/Dosing_GC-Optimize-Calculator.png"
          alt="GC OPTIMIZE calculator and tablets"
          width={236}
          height={226}
          className={styles.summary_success_modal_image}
        />
        <p className={styles.summary_success_modal_heading}>Thank you! Check your inbox for your summary.</p>
      </Modal>
    </>
  );
}

export default GcStepSummary;
